import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { memberFullName } from '../../library/membershipDisplay';
import { IPatientTherapist } from '../../library/models/clinic';
import ResourcesIds from '../../resources/ResourcesIds';
import { noTherapistsStyles, therapistsBoxStyles } from './patientTherapists.styles';
import { UserAvatar } from '../userAvatar/UserAvatar';

interface IPatientTherapistsProps {
    therapists: IPatientTherapist[];
    /**
     * The signed-in therapist, so their own chip can be told apart from a colleague's.
     * Optional because an admin looking at the same list is nobody's therapist.
     */
    currentUserId?: number;
}

/** Why this therapist has this patient -- the rule, said in one word. */
const reasonKeyFor = (therapist: IPatientTherapist): string => {
    if (therapist.viaEvaluation && therapist.viaService) {
        return ResourcesIds.PATIENTS_THERAPIST_VIA_BOTH;
    }

    return therapist.viaEvaluation
        ? ResourcesIds.PATIENTS_THERAPIST_VIA_EVALUATION
        : ResourcesIds.PATIENTS_THERAPIST_VIA_SERVICE;
};

/**
 * Who a patient belongs to.
 *
 * A patient belongs to the therapist who evaluated them, and to whoever provides a
 * service to them -- both at once where those are different people, which is the case
 * this exists to show. Before it, the only way to ask was the "my patients" toggle, and a
 * toggle can say whether a patient is yours but never that they are also somebody else's.
 *
 * Shared by the list and the patient's own file so the two cannot drift into saying
 * different things about the same patient.
 */
export const PatientTherapists = ({
    therapists,
    currentUserId,
}: IPatientTherapistsProps): JSX.Element => {
    const { t: translate } = useTranslation();

    if (therapists.length === 0) {
        // Nobody has claimed them: invited straight by an admin, with no evaluation and
        // nothing bought. Worth showing plainly rather than leaving the cell blank.
        return (
            <Typography variant="body2" sx={noTherapistsStyles}>
                {translate(ResourcesIds.PATIENTS_THERAPISTS_NONE)}
            </Typography>
        );
    }

    return (
        <Box sx={therapistsBoxStyles}>
            {therapists.map(therapist => {
                const name = memberFullName(therapist) || translate(ResourcesIds.PATIENTS_THERAPIST_UNNAMED);
                const isSelf = currentUserId !== undefined && therapist.id === currentUserId;

                // describeChild so the chip's accessible name stays the therapist's own:
                // by default MUI makes the tooltip the name, which would have a screen
                // reader announce the reason and never the person.
                return (
                    <Tooltip key={therapist.id} describeChild title={translate(reasonKeyFor(therapist))}>
                        {/* Filled for the caller, outlined for everybody else: a therapist
                            scanning the list is looking for their own name first, and the
                            reason each name is there is one hover away. */}
                        <Chip
                            size="small"
                            label={name}
                            // `avatar` rather than `icon`: MUI insets and rounds it to the
                            // chip's own height, so the picture takes no extra room in a
                            // cell that already holds several of these side by side.
                            avatar={<UserAvatar person={therapist} size="xs" />}
                            color={isSelf ? 'primary' : 'default'}
                            variant={isSelf ? 'filled' : 'outlined'}
                        />
                    </Tooltip>
                );
            })}
        </Box>
    );
};
